<template>
  <UiContainer class="!px-0 md:!px-4 lg:!px-7">
    <div v-if="!loading && slides" class="group relative w-full">
      <UiButton
        v-if="slides.length > 1"
        icon
        class="prev-home-slider group !absolute left-6 z-10 hidden size-10 items-center justify-center overflow-hidden !rounded-full !border-none !bg-transparent !p-0 opacity-0 transition-opacity inset-y-center group-hover:opacity-100 md:flex lg:flex"
      >
        <div
          class="absolute inset-0 size-full rounded-full bg-primary opacity-45 backdrop-blur-lg transition-all"
        />
        <UiIcon
          name="chevron-down"
          class="size-4 rotate-90 text-icon-secondary transition-transform group-hover:-translate-x-px"
        />
      </UiButton>
      <UiSwiper>
        <Swiper
          ref="swiper"
          :modules="[Navigation, Autoplay, Pagination, Keyboard]"
          :autoplay="{
            delay: getMilliseconds.inSeconds(5),
            pauseOnMouseEnter: true,
            disableOnInteraction: false
          }"
          :pagination="{ clickable: true, bulletElement: 'button', el: '.home-slider-pagination' }"
          :navigation="{ prevEl: '.prev-home-slider', nextEl: '.next-home-slider' }"
          :keyboard="{
            enabled: true,
            onlyInViewport: true
          }"
          centered-slides
          watch-overflow
          :loop="slides!.length > 2"
          class="overflow-hidden md:rounded-3xl"
          @autoplay-pause="swiperPaused = true"
          @autoplay-resume="swiperPaused = false"
        >
          <SwiperSlide v-for="slide in slides" :key="slide.id">
            <SlideItem :value="slide" />
          </SwiperSlide>
        </Swiper>
      </UiSwiper>
      <UiButton
        v-if="slides.length > 1"
        icon
        class="next-home-slider group !absolute right-6 z-10 hidden size-10 items-center justify-center overflow-hidden !rounded-full !border-none !bg-transparent !p-0 opacity-0 transition-opacity inset-y-center group-hover:opacity-100 md:flex lg:flex"
      >
        <div
          class="absolute inset-0 size-full rounded-full bg-primary opacity-45 backdrop-blur-lg transition-all"
        />
        <UiIcon
          name="chevron-down"
          class="size-4 -rotate-90 text-icon-secondary transition-transform group-hover:translate-x-px"
        />
      </UiButton>
      <div
        :class="{ 'swiper-paused': swiperPaused }"
        class="home-slider-pagination mt-2 flex w-full justify-center gap-4"
      />
    </div>
    <HomeSliderSkeleton v-else />
  </UiContainer>
</template>

<script lang="ts" setup>
import { Navigation, Autoplay, Pagination, Keyboard } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { computed, ref } from 'vue'
import { type Slide, SlideItem } from '@/5_entities/Slide'
import { getMilliseconds, useBreakpoint } from '@/6_shared/lib'
import { UiButton, UiContainer, UiIcon, UiSwiper } from '@/6_shared/ui'
import HomeSliderSkeleton from './HomeSliderSkeleton.vue'

type PropType = {
  desktop?: Slide[]
  mobile?: Slide[]
  loading: boolean
}

const props = defineProps<PropType>()

const swiperPaused = ref(false)
const {
  md: [isMdAndUp]
} = useBreakpoint()

const slidesForDesktop = computed(() => props.desktop?.filter((item) => item.posterImage))
const slidesForMobile = computed(() => props.mobile?.filter((item) => item.mobilePosterImage))
const slides = computed(() => (isMdAndUp.value ? slidesForDesktop.value : slidesForMobile.value))
</script>

<style scoped>
:deep(.swiper-pagination-bullet) {
  @apply size-2 md:size-3 !mx-0 bg-sliderBullet rounded-full overflow-hidden relative;
}

:deep(.swiper-pagination-bullet::before) {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  transition: width 0.1s linear;
  background: var(--background-sliderBullet-active);
  inset: 0;
  max-width: 0;
}

:deep(.swiper-pagination-bullet-active) {
  @apply w-4.5 md:w-6;
}

:deep(.swiper-pagination-bullet-active::before) {
  animation: activeSlide 5s forwards linear;
}

.swiper-paused :deep(.swiper-pagination-bullet-active::before) {
  animation-play-state: paused;
}

@keyframes activeSlide {
  from {
    max-width: 0;
  }
  to {
    max-width: 100%;
  }
}
</style>
